import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid2 as Grid } from '@mui/material';
import ContentViewComponent from '../../../../Component/ContentViewComponent/ContentViewComponent';
import { getRoutePathname } from '../../../../Handler/RouteHandler';
import { initFormHandler } from '../../../../Handler/FormHandler';
import TextFieldComponent from '../../../../Component/TextFieldComponent/TextFieldComponent';
import WysiwygComponent from '../../../../Component/WysiwygComponent/WysiwygComponent';
import SelectComponent from '../../../../Component/SelectComponent/SelectComponent';
import MultipleSelectComponent from '../../../../Component/MultipleSelectComponent/MultipleSelectComponent';
import CheckboxComponent from '../../../../Component/CheckboxComponent/CheckboxComponent';
import ButtonComponent from '../../../../Component/ButtonComponent/ButtonComponent';
import ApiHandler from '../../../../Handler/ApiHandler';
import { dispatch } from '../../../../App';
import { SnackbarOpen } from '../../../../Reducer/SnackBarReducer/SnackBarSlice';
import { formatDuration } from '../../../../Common/DurationCommon';

const getPhotoUrl = (path) => {
    if (!path) {
        return '';
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    if (path.startsWith('/api/')) {
        return path;
    }

    return `/api${path}`;
};

function Prestation() {
    const [loading, setLoading] = React.useState(true);
    const [breadcrumbs, setBreadcrumbs] = React.useState({});
    const [isEdit, setIsEdit] = React.useState(true);
    const [info, setInfo] = React.useState();
    const [optionsPrestationGroup, setOptionsPrestationGroup] = React.useState([]);
    const [optionsComplements, setOptionsComplements] = React.useState([]);
    const [existingPhotos, setExistingPhotos] = React.useState([]);
    const [newPhotos, setNewPhotos] = React.useState([]);
    const params = useParams();
    const navigate = useNavigate();

    const [form, setForm] = React.useState({
        prestationGroupId: {
            name: "prestationGroupId",
            label: "Groupe de prestation",
            textHelper: "Optionnel.",
            type: "integer",
            defaultValue: "",
            options: {},
        },
        complementIds: {
            name: "complementIds",
            label: "Compléments autorisés",
            textHelper: "Optionnel. Ces prestations seront proposées en ajout lors de la réservation.",
            type: "array",
            defaultValue: [],
            options: {},
        },
        label: {
            name: "label",
            label: "Libellé",
            textHelper: "Saisissez le libellé.",
            type: "text",
            defaultValue: "",
            options: { validation: ["required"], min: 1, max: 255 },
        },
        duration: {
            name: "duration",
            label: "Durée (minutes)",
            textHelper: "Saisissez la durée en minutes.",
            type: "integer",
            defaultValue: "",
            options: { validation: ["required", "integer"], min: 1 },
        },
        planningDuration: {
            name: "planningDuration",
            label: "Temps en plus (minutes)",
            textHelper: "Optionnel. Temps de préparation/nettoyage, non affiché au client.",
            type: "integer",
            defaultValue: "",
            options: { validation: ["integer"], min: 0 },
        },
        price: {
            name: "price",
            label: "Prix (€)",
            textHelper: "Saisissez le prix de la prestation.",
            type: "float",
            defaultValue: "",
            options: { validation: ["required", "float"], min: 0 },
        },
        isBookable: {
            name: "isBookable",
            label: "Prestation réservable",
            textHelper: "Décochez pour garder la prestation visible sans réservation en ligne.",
            type: "boolean",
            defaultValue: true,
            options: {},
        },
        showDuration: {
            name: "showDuration",
            label: "Afficher la durée au client",
            textHelper: "Décochez pour masquer la durée sur le site public.",
            type: "boolean",
            defaultValue: true,
            options: {},
        },
        about: {
            name: "about",
            label: "À propos de cette prestation",
            textHelper: "Description de la prestation.",
            type: "text",
            defaultValue: "",
            options: {},
        },
        description: {
            name: "description",
            label: "Description",
            textHelper: "Description courte affichée sur le catalogue.",
            type: "text",
            defaultValue: "",
            options: {},
        },
        goodToKnow: {
            name: "goodToKnow",
            label: "Bon à savoir",
            textHelper: "Informations utiles pour le client.",
            type: "text",
            defaultValue: "",
            options: {},
        }
    });
    const handlerForm = initFormHandler(form, setForm);
    React.useEffect(() => {
        handlerForm.start();
    }, [handlerForm]);

    React.useEffect(() => {
        setBreadcrumbs({
            title: 'Configuration',
            context: isEdit ? 'Éditer une prestation' : 'Ajouter une prestation',
            description: '',
            links: [
                { label: "Liste des prestations", path: getRoutePathname("configuration_prestation_list") }
            ]
        });

        setTimeout(() => {
            setLoading(false);
        }, 1500);
    }, [isEdit]);

    React.useEffect(() => {
        ApiHandler.get(
            { route: 'configuration_prestation_group_list' },
            (response) => {
                if (response.status === 200) {
                    const options = (response.data || []).map((item) => ({
                        value: item.id,
                        label: item.label,
                    }));
                    setOptionsPrestationGroup(options);
                }
            },
        );
    }, []);

    React.useEffect(() => {
        ApiHandler.get(
            { route: 'configuration_prestation_list' },
            (response) => {
                if (response.status === 200) {
                    const currentId = params.id !== 'add' ? parseInt(params.id, 10) : null;
                    const options = (response.data || [])
                        .filter((item) => !currentId || item.id !== currentId)
                        .map((item) => ({
                            value: item.id,
                            label: `${item.label} (+${formatDuration(item.duration)})`,
                        }));
                    setOptionsComplements(options);
                }
            },
        );
    }, [params.id]);

    React.useEffect(() => {
        if (params.id === 'add') {
            setIsEdit(false);
            setExistingPhotos([]);
        }
        else {
            ApiHandler.get({
                route: 'configuration_prestation_get',
                params: { id: params.id },
            },
                (response) => {
                    if (response.status === 200) {
                        handlerForm.setDataApi(response.data);
                        setInfo(response.data);
                        setExistingPhotos(Array.isArray(response.data.photos) ? response.data.photos : []);
                    }
                    else {
                        dispatch(
                            SnackbarOpen({
                                text:
                                    response.error && response.error.message
                                        ? response.error.message
                                        : "Une erreur inattendu s'est produite.",
                                variant: "error",
                            }),
                        );
                        navigate(getRoutePathname('configuration_prestation_list'));
                    }
                });
        }
    }, [handlerForm, navigate, params.id]);

    React.useEffect(() => {
        return () => {
            newPhotos.forEach((photo) => URL.revokeObjectURL(photo.preview));
        };
    }, [newPhotos]);

    const handleAddPhotos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) {
            return;
        }

        const validFiles = files.filter((file) => file.type.startsWith('image/'));
        if (validFiles.length !== files.length) {
            dispatch(
                SnackbarOpen({
                    text: "Seuls les fichiers image sont autorisés.",
                    variant: "error",
                }),
            );
        }

        const nextPhotos = validFiles.map((file) => ({
            file: file,
            name: file.name,
            preview: URL.createObjectURL(file),
        }));

        setNewPhotos((prev) => [...prev, ...nextPhotos]);
        event.target.value = "";
    };

    const removeExistingPhoto = (photoPath) => {
        setExistingPhotos((prev) => prev.filter((photo) => photo !== photoPath));
    };

    const removeNewPhoto = (indexToRemove) => {
        setNewPhotos((prev) => {
            const toRemove = prev[indexToRemove];
            if (toRemove && toRemove.preview) {
                URL.revokeObjectURL(toRemove.preview);
            }

            return prev.filter((_, index) => index !== indexToRemove);
        });
    };

    const save = () => {
        if (handlerForm.checkError() < 1) {
            handlerForm.setFormLoading(true);
            const data = handlerForm.getData();
            data.prestationGroupId = data.prestationGroupId ? parseInt(data.prestationGroupId, 10) : null;
            data.complementIds = Array.isArray(data.complementIds)
                ? data.complementIds
                    .map((value) => parseInt(value, 10))
                    .filter((value) => Number.isInteger(value) && value > 0)
                : [];
            data.photos = existingPhotos;

            const files = newPhotos.length > 0
                ? { photoFiles: newPhotos.map((item) => item.file) }
                : null;

            ApiHandler.post(
                {
                    route: isEdit ? "configuration_prestation_edit" : "configuration_prestation_add",
                    params: isEdit ? { id: info.id } : "",
                    data: data,
                    file: files,
                },
                (response) => {
                    if (response.status === 200 || response.status === 201) {
                        if (response.status === 201) {
                            navigate(getRoutePathname('configuration_prestation', { id: response.data.id }));
                        }
                        setNewPhotos([]);
                        dispatch(
                            SnackbarOpen({
                                text: isEdit ? "Prestation modifiée avec succès" : "Prestation créée avec succès",
                                variant: "success",
                            }),
                        );
                    } else if (response.status === 400) {
                        handlerForm.setErrorApi(response.error);
                    } else {
                        dispatch(
                            SnackbarOpen({
                                text:
                                    response.error && response.error.message
                                        ? response.error.message
                                        : "Une erreur inattendu s'est produite.",
                                variant: "error",
                            }),
                        );
                    }
                    handlerForm.setFormLoading(false);
                },
            );
        }
    };

    return (
        <ContentViewComponent loading={loading} breadcrumbs={breadcrumbs}>
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <TextFieldComponent id={"label"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <SelectComponent id={"prestationGroupId"} options={optionsPrestationGroup} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <MultipleSelectComponent id={"complementIds"} options={optionsComplements} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <TextFieldComponent id={"duration"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <TextFieldComponent id={"planningDuration"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <TextFieldComponent id={"price"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CheckboxComponent id={"isBookable"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                    <CheckboxComponent id={"showDuration"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <WysiwygComponent id={"about"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <WysiwygComponent id={"description"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <WysiwygComponent id={"goodToKnow"} handler={handlerForm} />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <div style={{ marginTop: 16 }}>
                        <label htmlFor="prestation-photos-input" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                            Photos de la prestation
                        </label>
                        <input
                            id="prestation-photos-input"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleAddPhotos}
                            style={{ marginBottom: 12 }}
                        />

                        {(existingPhotos.length > 0 || newPhotos.length > 0) && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                                {existingPhotos.map((photoPath, index) => (
                                    <div key={`existing-photo-${photoPath}-${index}`} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
                                        <img
                                            src={getPhotoUrl(photoPath)}
                                            alt={`Visuel ${index + 1}`}
                                            style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                                        />
                                        <ButtonComponent
                                            style={{ width: '100%', marginTop: 8 }}
                                            outlined
                                            label={"Retirer"}
                                            onClick={() => removeExistingPhoto(photoPath)}
                                        />
                                    </div>
                                ))}
                                {newPhotos.map((item, index) => (
                                    <div key={`new-photo-${index}`} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 8 }}>
                                        <img
                                            src={item.preview}
                                            alt={item.name}
                                            style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                                        />
                                        <ButtonComponent
                                            style={{ width: '100%', marginTop: 8 }}
                                            outlined
                                            label={"Retirer"}
                                            onClick={() => removeNewPhoto(index)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <ButtonComponent style={{ width: '100%', marginTop: '20px' }} label={"Enregistrer"} onClick={save} />
                </Grid>
            </Grid>
        </ContentViewComponent>
    );
}

export default Prestation;
